/* eslint-disable no-unused-vars */
import _ from 'underscore';
import { defineConstProperty } from '@bingads-webui/reflection';
import { Registry } from '@bingads-webui/registry';
import {
  ParameterInfo,
  PropertyInfo,
  CallableTypeContext,
  ObjectTypeContext,
  TypeOptions,
  TypeRegistry,
  OneOfConstructor,
  ResolveType,
  ParameterConstructor,
  PropertyConstructor,
  CollectionTypeOptions,
} from '../types';

export const ONEOF_TYPE_PREFIX = '@ONEOF';
const COLLECTION_TYPE_POSTFIX = '@COLL';

function isOneOfType(typeName: string): boolean {
  return typeName.indexOf(ONEOF_TYPE_PREFIX) === 0;
}

function hasPostfix(str: string, postfix: string): boolean {
  return str.slice(-postfix.length) === postfix;
}

function removePostfix(str: string, postfix: string): string {
  return str.slice(0, -postfix.length);
}

export function createResolveType(
  oneOfTypes: Map<string, InstanceType<OneOfConstructor>>,
  types: TypeRegistry,
  OneOfType: OneOfConstructor
) {
  return function resolveType(name: string, namespace: string): any {
    if (hasPostfix(name, COLLECTION_TYPE_POSTFIX)) {
      return resolveType(removePostfix(name, COLLECTION_TYPE_POSTFIX), namespace)
        .collectionType;
    }

    if (isOneOfType(name)) {
      if (!oneOfTypes.has(name)) {
        const prefixLength = ONEOF_TYPE_PREFIX.length + 1;
        const typeNames = name
          .substr(prefixLength, name.length - prefixLength - 1)
          .split(',');
        oneOfTypes.set(name, new OneOfType({ typeNames }));
      }

      return oneOfTypes.get(name);
    }

    return types.resolve(name, namespace);
  };
}

/**
 * @typedef {Object} ParameterInfo
 * @property {String} typeName - The name of the parameter's type
 *
 * @memberof CallableType#
 * @this CallableType
 * @param {Object.<String, ParameterInfo>} parameters - The parameters to compile
 * @param {Object.Parameter} Parameter - The Parameter object
 * @return {Object.<String, Parameter>} A mapping for name to compiled Parameters
 */
export function compileParameters(
  parameters: Record<string, ParameterInfo>,
  Parameter: ParameterConstructor = Object as any
): Record<string, typeof Parameter> {
  const { namespace } = this as ObjectTypeContext;

  return _.mapObject(parameters, ({ typeName }, name) => new Parameter({ name, namespace, typeName }));
}

/**
 * @typedef {Object} PropertyInfo
 * @property {String} typeName - The name of the propertie's type
 *
 * @memberof ObjectType#
 * @this ObjectType
 * @param {Object.<String, PropertyInfo>} properties - The properties to be compiled
 * @param {Object.Property} Property - The Property object
 * @return {Object.<String, Property>} a mapping for name to compiled Properties
 */
export function compileProperties(
  properties: Record<string, PropertyInfo>,
  Property: PropertyConstructor = Object as any
): Record<string, typeof Property> {
  const { namespace } = this as ObjectTypeContext;

  return _.mapObject(properties, ({ typeName }, name) => new Property({
    name,
    typeName,
    namespace,
  }));
}

export function createType(types: TypeRegistry) {
  return class Type {
    constructor({ name }: TypeOptions) {
      (_.chain(this) as any)
        .defineConstProperty('name', name)
        .defineConstProperty('namespace', Registry.getNamespace(name))
        .defineConstProperty('shortName', Registry.getShortName(name))
        .value();
      types.register(this);
    }
  };
}

export function createPrimitiveType(Superclass: any = Object) {
  return class PrimitiveType extends Superclass {
    constructor({
      name,
      jsType,
    }: {
      name: string;
      jsType: any;
    }) {
      super({ name });
      defineConstProperty(this, 'jsType', jsType);
    }
  };
}

export function createProperty(resolveType: ResolveType) {
  return class Property {
    constructor({
      name,
      typeName,
      namespace,
    }: {
      name: string;
      typeName: string;
      namespace: string;
    }) {
      (_.chain(this) as any)
        .defineConstProperty('name', name)
        .defineConstProperty('typeName', typeName)
        .defineProducedProperty('type', () => resolveType(typeName, namespace))
        .value();
    }
  };
}

export function createParameter(resolveType: ResolveType) {
  return class Parameter {
    constructor({
      name,
      typeName,
      namespace,
    }: {
      name: string;
      typeName: string;
      namespace: string;
    }) {
      (_.chain(this) as any)
        .defineConstProperty('name', name)
        .defineConstProperty('typeName', typeName)
        .defineProducedProperty('type', () => resolveType(typeName, namespace))
        .value();
    }
  };
}

export function createCollectionType(Superclass: any = Object, resolveType: ResolveType) {
  // eslint-disable-next-line no-redeclare, no-unused-vars
  interface CollectionType {
    elementTypeName: string;
    namespace: string;
  }
  // eslint-disable-next-line no-redeclare, no-shadow
  return class CollectionType extends Superclass {
    constructor({
      properties,
      baseTypeName,
      elementTypeName,
    }: CollectionTypeOptions) {
      super({
        name: CollectionType.collectionTypeName(elementTypeName),
        properties,
        baseTypeName,
      });
      (_.chain(this) as any)
        .defineConstProperty('elementTypeName', elementTypeName)
        .defineProducedProperty('elementType', () => resolveType(this.elementTypeName, this.namespace))
        .value();
    }

    static collectionTypeName(typeName: string) {
      return `${typeName}${COLLECTION_TYPE_POSTFIX}`;
    }
  };
}
